/**
 * jsToJq.js — heuristic JavaScript → JQ converter for the SCRIPT-to-
 * data_transform conversion affordance.
 *
 * Why heuristic and not a real parser? Most Conductor INLINE scripts
 * we see in imported workflows are short pure-functional one-liners
 * over a single input object: `$.foo.map(...)`, `$.foo.filter(...)`,
 * `$.foo.length`, `Object.values($.foo)`, simple property access. A
 * proper Babel/AST pipeline would handle every JS expression
 * faithfully but pulls a 200KB parser into the canvas frontend for
 * a feature most users will hit twice. The heuristic regex pipeline
 * below covers the patterns we've actually observed in real Conductor OSS
 * imports and tags everything else with low confidence so the user
 * knows to review the suggestion.
 *
 * What the converter recognises (mapped to JQ):
 *   $.foo                       → .foo
 *   $.foo.bar                   → .foo.bar
 *   $.foo[0]                    → .foo[0]
 *   $.foo.length                → .foo | length
 *   $.foo.map(p => p.x)         → .foo | map(.x)
 *   $.foo.map(p => ({...}))     → .foo | map({...})    (object keys quoted)
 *   $.foo.filter(p => p.x === Y)→ .foo | map(select(.x == Y))
 *   Object.values($.foo)        → [.foo[]]
 *   Object.keys($.foo)          → [.foo | keys[]]
 *   JSON.parse(x)               → (x | fromjson)
 *   JSON.stringify(x)           → (x | tojson)
 *   (function() { return X; })()→ X        (IIFE unwrap)
 *   (() => X)()                 → X        (arrow IIFE unwrap)
 *
 * What it explicitly tags as low confidence:
 *   - Multi-statement bodies (semicolons not inside string literals)
 *   - var / let / const declarations
 *   - new / typeof / instanceof / Date.now / Math.* / regex literals
 *   - Anything residual `function` or `=>` after the unwrap pass
 *
 * The converter never mutates the input; it returns a new object
 * with the suggested JQ, a confidence rating, and a list of caveats
 * the user should read before confirming.
 *
 * @typedef  {Object} JsToJqResult
 * @property {string}            jq          — the suggested JQ expression
 * @property {'high'|'medium'|'low'} confidence — how trustworthy the suggestion is
 * @property {string[]}          notes       — caveats / pattern detections
 * @property {string}            originalJs  — the input passed in (echoed back)
 */

const RESERVED_TOKENS = /\b(var|let|const|new|typeof|instanceof|throw|try|catch|finally|while|for|if|else|switch|case|break|continue|return)\b/;

/**
 * Convert a snippet of Conductor-style INLINE JavaScript into a
 * suggested JQ expression. Lossy — review the result before confirming.
 *
 * @param {string} jsBody — the JavaScript script body
 * @returns {JsToJqResult}
 */
export function jsToJq(jsBody) {
  const original = String(jsBody == null ? '' : jsBody);
  if (!original.trim()) {
    return {
      jq: '.',
      confidence: 'low',
      notes: ['Empty script — defaulting to identity (`.`).'],
      originalJs: original,
    };
  }

  const notes = [];
  let confidence = 'high';
  let s = original.trim();

  // ── Strip IIFE wrappers ───────────────────────────────────────────
  // (function() { return X; })()  →  X
  const fnIife = s.match(/^\s*\(\s*function\s*\([^)]*\)\s*\{\s*return\s+([\s\S]*?)\s*;?\s*\}\s*\)\s*\(\s*\)\s*;?\s*$/);
  if (fnIife) {
    s = fnIife[1].trim();
    notes.push('Unwrapped function-IIFE.');
  } else {
    // (() => X)() or (() => { return X; })()
    const arrowIifeReturn = s.match(/^\s*\(\s*\(\s*\)\s*=>\s*\{\s*return\s+([\s\S]*?)\s*;?\s*\}\s*\)\s*\(\s*\)\s*;?\s*$/);
    const arrowIifeExpr   = s.match(/^\s*\(\s*\(\s*\)\s*=>\s*([\s\S]*?)\s*\)\s*\(\s*\)\s*;?\s*$/);
    if (arrowIifeReturn) {
      s = arrowIifeReturn[1].trim();
      notes.push('Unwrapped arrow-IIFE (return-style).');
    } else if (arrowIifeExpr) {
      s = arrowIifeExpr[1].trim();
      notes.push('Unwrapped arrow-IIFE (expression-style).');
    }
  }

  // Strip a trailing semicolon if any.
  s = s.replace(/;+\s*$/, '');

  // Strip a single pair of redundant outer parens, only if they
  // balance to enclose the entire expression (so we don't strip
  // grouping in `(a) + (b)`).
  if (s.startsWith('(') && s.endsWith(')')) {
    let depth = 0;
    let allOuter = true;
    for (let i = 0; i < s.length; i++) {
      if (s[i] === '(') depth++;
      else if (s[i] === ')') depth--;
      if (depth === 0 && i < s.length - 1) { allOuter = false; break; }
    }
    if (allOuter) s = s.slice(1, -1).trim();
  }

  // ── Pattern translation pass ──────────────────────────────────────

  // JSON.parse/stringify before $. so $.x|tojson chains work.
  if (/\bJSON\.parse\b/.test(s)) {
    s = s.replace(/JSON\.parse\s*\(([\s\S]+?)\)/g, '($1 | fromjson)');
    notes.push('JSON.parse(…) → (… | fromjson)');
  }
  if (/\bJSON\.stringify\b/.test(s)) {
    s = s.replace(/JSON\.stringify\s*\(([\s\S]+?)\)/g, '($1 | tojson)');
    notes.push('JSON.stringify(…) → (… | tojson)');
  }

  // Object.values($.foo) → [.foo[]]
  s = s.replace(/Object\.values\s*\(\s*\$\.([a-zA-Z_][\w.]*)\s*\)/g, '[.$1[]]');
  // Object.keys($.foo)   → [.foo | keys[]]
  s = s.replace(/Object\.keys\s*\(\s*\$\.([a-zA-Z_][\w.]*)\s*\)/g, '[.$1 | keys[]]');
  // Generic Object.values(x) / Object.keys(x) when x isn't $.…
  if (/Object\.(values|keys)\s*\(/.test(s)) {
    s = s.replace(/Object\.values\s*\(\s*([\s\S]+?)\s*\)/g, '[($1)[]]');
    s = s.replace(/Object\.keys\s*\(\s*([\s\S]+?)\s*\)/g, '[($1) | keys[]]');
    notes.push('Object.values/keys(…) translated to JQ pipe-explode.');
  }

  // $.foo.map(p => ({ k: p.x, … }))  →  .foo | map({ "k": .x, … })
  s = s.replace(
    /\$\.([a-zA-Z_][\w.]*)\s*\.\s*map\s*\(\s*([a-zA-Z_]\w*)\s*=>\s*\(\s*\{([\s\S]*?)\}\s*\)\s*\)/g,
    (_m, path, pname, body) => {
      const adjusted = body
        .replace(new RegExp(`\\b${pname}\\.([a-zA-Z_][\\w.]*)`, 'g'), '.$1')
        .replace(/(^|[,{\s])([a-zA-Z_]\w*)\s*:/g, (mm, lead, key) => `${lead}"${key}":`);
      return `.${path} | map({${adjusted.trim()}})`;
    }
  );

  // $.foo.map(p => p.x)  →  .foo | map(.x)
  s = s.replace(
    /\$\.([a-zA-Z_][\w.]*)\s*\.\s*map\s*\(\s*([a-zA-Z_]\w*)\s*=>\s*([a-zA-Z_]\w*)\.([a-zA-Z_][\w.]*)\s*\)/g,
    (_m, path, pname, refName, prop) => {
      if (refName !== pname) return _m; // bail — references something other than the param
      return `.${path} | map(.${prop})`;
    }
  );

  // $.foo.filter(p => p.x === Y) → .foo | map(select(.x == Y))
  s = s.replace(
    /\$\.([a-zA-Z_][\w.]*)\s*\.\s*filter\s*\(\s*([a-zA-Z_]\w*)\s*=>\s*([\s\S]*?)\)\s*(?=$|[,;)\s|.+\-*/])/g,
    (_m, path, pname, cond) => {
      const adj = cond
        .replace(new RegExp(`\\b${pname}\\.([a-zA-Z_][\\w.]*)`, 'g'), '.$1')
        .replace(/===/g, '==')
        .replace(/!==/g, '!=');
      return `.${path} | map(select(${adj.trim()}))`;
    }
  );

  // $.foo.length  →  .foo | length
  s = s.replace(/\$\.([a-zA-Z_][\w.]*)\s*\.\s*length\b/g, '.$1 | length');

  // Now substitute remaining `$.` with `.` for raw property access.
  s = s.replace(/\$\.([a-zA-Z_][\w.]*)/g, '.$1');

  // ── Confidence assessment ─────────────────────────────────────────
  if (/=>|\bfunction\b/.test(s)) {
    notes.push('Residual JS lambda or `function` keyword — review and edit by hand.');
    confidence = 'low';
  }
  if (RESERVED_TOKENS.test(s)) {
    notes.push('Statement-level JS keywords detected (var/let/const/if/etc.) — JQ is expression-only; review.');
    confidence = 'low';
  }
  if (/[a-zA-Z_]\w*\s*\(/.test(s) && !/^\s*[\[\.\{(]/.test(s)) {
    // function-call-shaped output that doesn't start with a JQ-ish token
    notes.push('Output starts with what looks like a function call — JQ expressions usually start with `.` / `[` / `{`. Verify.');
    if (confidence === 'high') confidence = 'medium';
  }
  if (/\bDate\.|\bMath\.|\bRegExp\b|new\s+Date/.test(s)) {
    notes.push('Time / math / regex APIs detected — JQ has no clock or Math.random; if your script depends on them, convert to a worker_task instead.');
    confidence = 'low';
  }
  if (s.indexOf(';') !== -1) {
    notes.push('Multi-statement body detected — JQ expressions are pipelines, not statement sequences. Manual rewrite likely required.');
    confidence = 'low';
  }

  if (notes.length === 0) {
    notes.push('Pattern-matched cleanly. Run the workflow once to confirm the JQ output shape matches what downstream nodes expect.');
  }

  return { jq: s.trim(), confidence, notes, originalJs: original };
}

/**
 * Apply a confirmed conversion to a canvas node — produces the patch
 * the canvas's `updateNode` accepts. Pure function, no side effects;
 * the caller passes the result to onUpdate(...).
 */
export function buildJqConversionPatch(jqExpression, existingConfig) {
  return {
    type: 'data_transform',
    config: {
      ...(existingConfig || {}),
      transformType: 'jq',
      expression: jqExpression,
      // Keep the original script body archived under
      // _convertedFromScript so an operator can audit what the node
      // used to be without trawling git history.
      _convertedFromScript:
        (existingConfig && (existingConfig.expression || existingConfig.script))
          || (existingConfig && existingConfig._convertedFromScript)
          || null,
    },
  };
}
